/* eslint-disable no-undef */
import { logger } from '@biproxi/logger';
import React from 'react';
import styled from '@emotion/styled';
import IListingMostViews from '@biproxi/models/interfaces/IListingMostViews';
import { useQuery } from '@apollo/client';
import UserUtil from '@biproxi/models/utils/UserUtil';
import LISTING_MOST_VIEWS_ANALYTICS from '../../../graphql/queries/cubeListingMostViews.query';
import Icon from '../../../elements/Icon';
import Text, { TextTypesEnum } from '../../../elements/Text';
import Colors from '../../../styles/Colors';
import Icons from '../../../elements/Icons';
import ProfileImage, {
  ProfileImageTypes,
} from '../../ProfileImage';
import { media } from '../../../utils/MediaQuery';
import Loader from '../../../elements/Loader';

const MostViewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  width: 100%;
  height: 284px;
  border: 1px solid ${Colors.Grey300};
  border-radius: 16px;
  box-sizing: border-box;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  ${media.mobile} {
    width: 100%;
    height: 292px;
  }
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  height: 100%;
`;

const UserViewContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  margin: 4px 0px;
  padding: 0px;
  width: 100%;
`;

const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
  width: 100%;
`;

const NoDataIconContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: ${Colors.Grey200};
  border-radius: 100px;
  margin: 4px 0;
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type ComponentProps = {
  listingId: string;
  listingUserId: string;
};

const MostViewsCard: React.FC<ComponentProps> = ({ listingId, listingUserId }) => {
  /** GraphQL */
    interface Data {
    listingViewsPerUser: IListingMostViews[];
  }
  interface Vars {
    params: {
      listingId: string
      listingUserId: string
    };
  }

  const { data, loading: isDataLoading } = useQuery<Data, Vars>(
    LISTING_MOST_VIEWS_ANALYTICS,
    {
      variables: {
        params: {
          listingId,
          listingUserId,
        },
      },
      onError: (error) => {
        logger.error('LISTING_MOST_VIEWS_ANALYTICS error', error);
      },
    },
  );

  const mostViewsData: IListingMostViews[] = data?.listingViewsPerUser ?? [];

  return (
    <MostViewsContainer>
      <Text type={TextTypesEnum.Bold16} margin="0 0 16px 0">
        Most views
      </Text>
      <StatsContainer>
        {isDataLoading ? (
          <LoaderContainer>
            <Loader color={Colors.Brand700 || Colors.Blurple700} />
          </LoaderContainer>
        ) : mostViewsData && mostViewsData.length ? (
          mostViewsData?.map(
            (listingViewInfo: IListingMostViews, index: number) => {
              const user = listingViewInfo?.user;
              return (
                <UserViewContainer key={index}>
                  <UserInfoContainer>
                    <ProfileImage
                      user={listingViewInfo.user}
                      type={ProfileImageTypes.Small}
                    />
                    <Text type={TextTypesEnum.Regular14} color={Colors.Black}>
                      {user ? UserUtil.fullName(user) : 'Anonymous'}
                    </Text>
                  </UserInfoContainer>
                  <Text type={TextTypesEnum.Regular14} color={Colors.Grey500}>
                    {listingViewInfo.views}
                    {' '}
                    {listingViewInfo.views > 1 ? 'views' : 'view'}
                  </Text>
                </UserViewContainer>
              );
            },
          )
        ) : (
          <NoDataContainer>
            <NoDataIconContainer>
              <Icon icon={Icons.EyeLight} color={Colors.Grey700} size={24} />
            </NoDataIconContainer>
            <Text type={TextTypesEnum.Regular14} color={Colors.Grey700}>
              No views yet
            </Text>
          </NoDataContainer>
        )}
      </StatsContainer>
    </MostViewsContainer>
  );
};

export default MostViewsCard;
